import { resampleTimeline } from './smooth.js';

export interface ChartPoint {
  t: number;
  v: number;
}

export interface InfraChartOptions {
  title: string;
  unit: string;
  min?: number;
  max?: number;
  color?: string;
  fill?: boolean;
}

let modalEl: HTMLElement | null = null;
let titleEl: HTMLElement | null = null;
let hintEl: HTMLElement | null = null;
let svgEl: SVGSVGElement | null = null;
let openChartKey: string | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const CHART_SLOTS = 28;
const CHART_LERP = 0.16;
const SVG_NS = 'http://www.w3.org/2000/svg';
const WIDTH = 320;
const HEIGHT = 140;
const PAD_L = 34;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 28;
const PLOT_W = WIDTH - PAD_L - PAD_R;
const PLOT_H = HEIGHT - PAD_T - PAD_B;

function ensureModal(): void {
  if (modalEl) return;
  modalEl = document.getElementById('infra-chart-modal');
  titleEl = document.getElementById('infra-chart-title');
  hintEl = document.getElementById('infra-chart-hint');
  const svg = document.getElementById('infra-chart-svg');
  svgEl = svg instanceof SVGSVGElement ? svg : null;
  const closeBtn = document.getElementById('infra-chart-close');
  const backdrop = modalEl?.querySelector('.infra-chart-backdrop');
  closeBtn?.addEventListener('click', closeInfraChart);
  backdrop?.addEventListener('click', closeInfraChart);
}

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function mergePoints(a: ChartPoint[], b: ChartPoint[]): ChartPoint[] {
  const map = new Map<number, number>();
  for (const p of a) map.set(p.t, p.v);
  for (const p of b) map.set(p.t, p.v);
  return Array.from(map.entries())
    .map(([t, v]) => ({ t, v }))
    .sort((x, y) => x.t - y.t);
}

export function mergeChartHistory(server: ChartPoint[], client: ChartPoint[]): ChartPoint[] {
  return mergePoints(server, client);
}

/** Bucket-average series for stable line + even time axis (client polls every ~2s). */
export function prepareChartSeries(points: ChartPoint[], bucketMs = 30_000): ChartPoint[] {
  if (points.length === 0) return [];
  const sorted = mergePoints([], points);
  if (sorted.length <= 2) return sorted;

  const tMin = sorted[0].t;
  const tMax = sorted[sorted.length - 1].t;
  const span = tMax - tMin;
  let bucket = bucketMs;
  if (span < bucket * 3) {
    bucket = Math.max(10_000, Math.floor(span / 12));
  }

  const buckets = new Map<number, { sum: number; count: number; tLast: number }>();
  for (const p of sorted) {
    const key = Math.floor((p.t - tMin) / bucket);
    const cur = buckets.get(key) ?? { sum: 0, count: 0, tLast: p.t };
    cur.sum += p.v;
    cur.count += 1;
    cur.tLast = p.t;
    buckets.set(key, cur);
  }

  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, { sum, count, tLast }]) => ({
      t: tLast,
      v: Math.round((sum / count) * 10) / 10
    }));
}

function timeAxisTicks(tMin: number, tMax: number, count = 5): number[] {
  const span = Math.max(tMax - tMin, 60_000);
  const step = span / Math.max(count - 1, 1);
  const ticks: number[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < count; i++) {
    const t = i === count - 1 ? tMax : Math.round(tMin + step * i);
    const label = formatTime(t);
    if (seen.has(label)) continue;
    seen.add(label);
    ticks.push(t);
  }
  if (ticks.length === 0 || ticks[ticks.length - 1] !== tMax) {
    ticks.push(tMax);
  }
  return ticks;
}

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const n = value / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function svgEl_<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>
): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function animatePath(path: SVGPathElement, delayMs = 0): void {
  const len = path.getTotalLength();
  path.style.strokeDasharray = String(len);
  path.style.strokeDashoffset = String(len);
  path.style.opacity = '0.85';
  window.setTimeout(() => {
    path.style.transition =
      'stroke-dashoffset 0.9s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease';
    path.style.strokeDashoffset = '0';
    path.style.opacity = '1';
  }, delayMs);
}

function animateArea(area: SVGPathElement): void {
  area.style.opacity = '0';
  window.setTimeout(() => {
    area.style.transition = 'opacity 0.7s ease 0.15s';
    area.style.opacity = '1';
  }, 0);
}

class ChartAnimator {
  private display: ChartPoint[] = [];
  private target: ChartPoint[] = [];
  private viewTMin = 0;
  private viewTMax = 1;
  private displayMaxY = 1;
  private targetMaxY = 1;
  private options: InfraChartOptions | null = null;
  private raf: number | null = null;
  private built = false;
  private drawAnimated = false;
  private lineEl: SVGPathElement | null = null;
  private areaEl: SVGPathElement | null = null;
  private defsEl: SVGDefsElement | null = null;
  private gradId = '';
  private gridEls: SVGLineElement[] = [];
  private yLabelEls: SVGTextElement[] = [];
  private xLabelEls: SVGTextElement[] = [];
  private emptyEl: SVGTextElement | null = null;

  reset(): void {
    this.stop();
    this.built = false;
    this.drawAnimated = false;
    this.display = [];
    this.target = [];
    this.lineEl = null;
    this.areaEl = null;
    this.defsEl = null;
    this.gridEls = [];
    this.yLabelEls = [];
    this.xLabelEls = [];
    this.emptyEl = null;
    if (svgEl) {
      while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);
    }
  }

  stop(): void {
    if (this.raf != null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  setTarget(rawPoints: ChartPoint[], options: InfraChartOptions, instant = false): void {
    if (!svgEl) return;
    this.options = options;
    this.ensureStructure(options);

    let prepared = prepareChartSeries(rawPoints);
    if (options.unit === '°C') {
      prepared = prepared.map((p) => ({ ...p, v: Math.round(p.v) }));
    }

    this.target = resampleTimeline(prepared, CHART_SLOTS);
    this.targetMaxY = this.computeMaxY(this.target, options);

    if (instant || this.display.length === 0) {
      this.display = this.target.map((p) => ({ ...p }));
      this.displayMaxY = this.targetMaxY;
      if (this.target.length > 0) {
        this.viewTMin = this.target[0].t;
        this.viewTMax = this.target[this.target.length - 1].t;
      }
      this.paint(!instant && !this.drawAnimated);
      return;
    }

    this.startLoop();
  }

  private computeMaxY(points: ChartPoint[], options: InfraChartOptions): number {
    const minY = options.min ?? 0;
    if (options.max != null) return options.max;
    const peak = points.reduce((m, p) => Math.max(m, p.v), 0);
    return niceMax(Math.max(peak * 1.1, minY + 1));
  }

  private ensureStructure(options: InfraChartOptions): void {
    if (!svgEl || this.built) return;
    this.built = true;

    this.defsEl = svgEl_('defs', {}) as SVGDefsElement;
    svgEl.appendChild(this.defsEl);

    const gridLines = 4;
    for (let i = 0; i <= gridLines; i++) {
      const y = PAD_T + (PLOT_H * i) / gridLines;
      const grid = svgEl_('line', {
        x1: PAD_L,
        y1: y,
        x2: WIDTH - PAD_R,
        y2: y,
        stroke: 'rgba(255,255,255,0.06)',
        'stroke-width': 1,
        class: 'infra-chart-grid'
      }) as SVGLineElement;
      grid.style.opacity = '1';
      svgEl.appendChild(grid);
      this.gridEls.push(grid);

      const label = svgEl_('text', {
        x: PAD_L - 6,
        y: y + 4,
        fill: '#8e8e93',
        'font-size': 9,
        'text-anchor': 'end'
      }) as SVGTextElement;
      svgEl.appendChild(label);
      this.yLabelEls.push(label);
    }

    this.emptyEl = svgEl_('text', {
      x: WIDTH / 2,
      y: HEIGHT / 2,
      fill: '#8e8e93',
      'font-size': 12,
      'text-anchor': 'middle',
      class: 'infra-chart-empty'
    }) as SVGTextElement;
    this.emptyEl.textContent = 'Накапливаем данные…';
    this.emptyEl.style.display = 'none';
    svgEl.appendChild(this.emptyEl);

    const color = options.color || '#5b8def';
    if (options.fill) {
      this.gradId = 'infra-chart-fill-' + String(Date.now());
      const grad = svgEl_('linearGradient', { id: this.gradId, x1: 0, y1: 0, x2: 0, y2: 1 });
      grad.appendChild(svgEl_('stop', { offset: '0%', 'stop-color': color, 'stop-opacity': 0.42 }));
      grad.appendChild(svgEl_('stop', { offset: '100%', 'stop-color': color, 'stop-opacity': 0.02 }));
      this.defsEl.appendChild(grad);
      this.areaEl = svgEl_('path', {
        d: '',
        fill: `url(#${this.gradId})`,
        class: 'infra-chart-area'
      }) as SVGPathElement;
      svgEl.appendChild(this.areaEl);
    }

    this.lineEl = svgEl_('path', {
      d: '',
      fill: 'none',
      stroke: color,
      'stroke-width': 2,
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round',
      class: 'infra-chart-line'
    }) as SVGPathElement;
    svgEl.appendChild(this.lineEl);
  }

  private startLoop(): void {
    if (this.raf != null) return;

    const tick = (): void => {
      let moving = false;

      while (this.display.length < this.target.length) {
        const i = this.display.length;
        this.display.push({ ...this.target[i] });
      }
      if (this.display.length > this.target.length) {
        this.display.length = this.target.length;
      }

      for (let i = 0; i < this.target.length; i++) {
        const d = this.display[i];
        const t = this.target[i];
        const nv = d.v + (t.v - d.v) * CHART_LERP;
        const nt = d.t + (t.t - d.t) * CHART_LERP;
        if (Math.abs(t.v - nv) > 0.08 || Math.abs(t.t - nt) > 80) moving = true;
        d.v = nv;
        d.t = nt;
      }

      if (this.target.length > 0) {
        const tMin = this.target[0].t;
        const tMax = this.target[this.target.length - 1].t;
        if (Math.abs(this.viewTMin - tMin) > 50) moving = true;
        if (Math.abs(this.viewTMax - tMax) > 50) moving = true;
        this.viewTMin += (tMin - this.viewTMin) * CHART_LERP;
        this.viewTMax += (tMax - this.viewTMax) * CHART_LERP;
      }

      if (Math.abs(this.displayMaxY - this.targetMaxY) > 0.05) {
        moving = true;
        this.displayMaxY += (this.targetMaxY - this.displayMaxY) * CHART_LERP;
      }

      this.paint(false);

      if (moving) {
        this.raf = requestAnimationFrame(tick);
      } else {
        this.raf = null;
        this.display = this.target.map((p) => ({ ...p }));
        this.displayMaxY = this.targetMaxY;
        if (this.target.length > 0) {
          this.viewTMin = this.target[0].t;
          this.viewTMax = this.target[this.target.length - 1].t;
        }
        this.paint(false);
      }
    };

    this.raf = requestAnimationFrame(tick);
  }

  private paint(runOpenAnimation: boolean): void {
    if (!svgEl || !this.options || !this.lineEl) return;
    const options = this.options;
    const points = this.display;
    const hasData = points.length > 0;

    if (this.emptyEl) {
      this.emptyEl.style.display = hasData ? 'none' : 'block';
    }
    if (this.lineEl) this.lineEl.style.display = hasData ? 'block' : 'none';
    if (this.areaEl) this.areaEl.style.display = hasData && options.fill ? 'block' : 'none';

    const minY = options.min ?? 0;
    const maxY = this.displayMaxY;
    const spanY = Math.max(maxY - minY, 0.001);

    for (let i = 0; i < this.yLabelEls.length; i++) {
      const val = maxY - (spanY * i) / Math.max(this.yLabelEls.length - 1, 1);
      const label = this.yLabelEls[i];
      if (options.unit === '%') label.textContent = `${Math.round(val)}%`;
      else if (options.unit === '°C') label.textContent = `${Math.round(val)}°`;
      else label.textContent = `${Math.round(val)}`;
    }

    if (!hasData) return;

    const tMin = this.viewTMin;
    const tMax = this.viewTMax;
    const tSpan = Math.max(tMax - tMin, 1);

    const xAt = (t: number): number => PAD_L + ((t - tMin) / tSpan) * PLOT_W;
    const yAt = (v: number): number => PAD_T + PLOT_H - ((v - minY) / spanY) * PLOT_H;

    const coords = points.map((p) => `${xAt(p.t).toFixed(2)},${yAt(p.v).toFixed(2)}`);
    const linePath = `M ${coords.join(' L ')}`;
    this.lineEl.setAttribute('d', linePath);

    if (this.areaEl && options.fill && coords.length > 1) {
      const areaPath = `${linePath} L ${xAt(points[points.length - 1].t).toFixed(2)},${(PAD_T + PLOT_H).toFixed(2)} L ${xAt(points[0].t).toFixed(2)},${(PAD_T + PLOT_H).toFixed(2)} Z`;
      this.areaEl.setAttribute('d', areaPath);
    }

    const ticks = timeAxisTicks(tMin, tMax, 5);
    while (this.xLabelEls.length < ticks.length) {
      const label = svgEl_('text', {
        x: 0,
        y: HEIGHT - 6,
        fill: '#8e8e93',
        'font-size': 9,
        'text-anchor': 'middle'
      }) as SVGTextElement;
      svgEl.appendChild(label);
      this.xLabelEls.push(label);
    }
    while (this.xLabelEls.length > ticks.length) {
      const extra = this.xLabelEls.pop();
      extra?.remove();
    }
    for (let i = 0; i < ticks.length; i++) {
      const t = ticks[i];
      const x = xAt(t);
      const label = this.xLabelEls[i];
      label.setAttribute('x', String(x));
      label.setAttribute('transform', `rotate(-35 ${x} ${HEIGHT - 6})`);
      label.textContent = formatTime(t);
      label.style.transition = 'opacity 0.35s ease';
      label.style.opacity = '1';
    }

    if (runOpenAnimation && !this.drawAnimated) {
      this.drawAnimated = true;
      animatePath(this.lineEl, 80);
      if (this.areaEl) animateArea(this.areaEl);
    }
  }
}

const chartAnimator = new ChartAnimator();

function showModal(): void {
  if (!modalEl) return;
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  modalEl.hidden = false;
  requestAnimationFrame(() => modalEl?.classList.add('is-open'));
  document.body.classList.add('infra-chart-open');
}

function hideModal(): void {
  if (!modalEl) return;
  modalEl.classList.remove('is-open');
  closeTimer = setTimeout(() => {
    if (modalEl && !modalEl.classList.contains('is-open')) {
      modalEl.hidden = true;
    }
    closeTimer = null;
  }, 320);
  document.body.classList.remove('infra-chart-open');
}

export function openInfraChart(chartKey: string, points: ChartPoint[], options: InfraChartOptions): void {
  ensureModal();
  if (!modalEl || !titleEl || !hintEl) return;
  const wasOpen = openChartKey === chartKey && modalEl.classList.contains('is-open');
  if (openChartKey !== chartKey) {
    chartAnimator.reset();
  }
  openChartKey = chartKey;
  titleEl.textContent = options.title;
  hintEl.textContent =
    points.length > 1
      ? `История · нажмите вне графика, чтобы закрыть`
      : 'График заполнится после нескольких обновлений';
  chartAnimator.setTarget(points, options, wasOpen);
  showModal();
}

export function refreshOpenInfraChart(
  chartKey: string,
  points: ChartPoint[],
  options: InfraChartOptions
): void {
  if (openChartKey !== chartKey) return;
  if (!titleEl || !hintEl) return;
  hintEl.textContent =
    points.length > 1
      ? `История · нажмите вне графика, чтобы закрыть`
      : 'График заполнится после нескольких обновлений';
  chartAnimator.setTarget(points, options, false);
}

export function closeInfraChart(): void {
  openChartKey = null;
  chartAnimator.reset();
  hideModal();
}

export function getOpenInfraChartKey(): string | null {
  return openChartKey;
}

export const INFRA_CHART_PRESETS: Record<string, Omit<InfraChartOptions, 'title'> & { title?: string }> = {
  'home:cpu': { title: 'CPU · deep-design-server', unit: '%', min: 0, max: 100, color: '#a78bfa', fill: true },
  'home:ram': { title: 'RAM · deep-design-server', unit: '%', min: 0, max: 100, color: '#fb923c', fill: true },
  'home:temp': { title: 'Температура · deep-design-server', unit: '°C', min: 0, color: '#f87171', fill: false },
  'vps:cpu': { title: 'CPU · deep-design-vps', unit: '%', min: 0, max: 100, color: '#a78bfa', fill: true },
  'vps:ram': { title: 'RAM · deep-design-vps', unit: '%', min: 0, max: 100, color: '#fb923c', fill: true }
};

export function chartOptionsForPing(checkId: string, checkName: string): InfraChartOptions {
  return {
    title: `Пинг · ${checkName}`,
    unit: 'мс',
    min: 0,
    color: '#60a5fa',
    fill: true
  };
}
